import { MultipleChoices } from "@/components/PredictionMarket/MarketCardList/MultipleChoices";
import { MultipleChoicesGraph } from "@/components/PredictionMarket/MarketCardList/MultipleChoicesGraph";
import { PredictionChart } from "@/components/PredictionMarket/PredictionChart";
import { ChartList } from "@/components/PredictionMarket/PredictionChart/ChartList";
import { EntryShareButton } from "@/components/PredictionMarket/PredictionChart/EntryShareButton";
import { PredictionMarketOGP } from "@/components/PredictionMarket/PredictionMarketOGP";
import { Tag } from "@/components/PredictionMarket/Tag";
import { DeadlineTime } from "@/components/homepage/RoundCard/components/DeadlineTime";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { Skeleton } from "@/components/ui/skeleton";
import { useHandleError } from "@/hooks/useHandleError";
import { useAuthStore } from "@/store/auth.store";
import { formatNumberWithCommas } from "@/utils/formatNumberWithCommas";
import { ChevronUp, ClockIcon, GroupUserIcon, ThumbaCoin } from "@roman/shared/components/Icons";
import { useMyGetPredictionMarketRanking } from "@roman/shared/hooks/PredictionMarket/useGetMyPredictionMarketRanking";
import { useGetPredictionMarketHistory } from "@roman/shared/hooks/PredictionMarket/useGetPredictionMarketHistory";
import { useGetPredictionMarketRanking } from "@roman/shared/hooks/PredictionMarket/useGetPredictionMarketRanking";
import type {
  MarketRankEnded,
  MarketRankLive,
  PredictionMarketChoice,
  PredictionMarketSnapshot,
} from "@roman/shared/types/PredictionMarket";
import {
  PredictionMarketType,
  extractChoices,
  getPredictionMarketType,
  getYesNoLabel,
} from "@roman/shared/utils/predictionMarket/utils";
import type { Choice } from "@roman/shared/utils/predictionMarket/utils";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AllRankingsModal } from "./components/AllRankings";
import { ChoiceRanking } from "./components/ChoiceRanking";
import { DetailPageEntryPopup as EntryPopup } from "./components/DetailPageEntryPopup";
import RelatedPredictionMarket from "./components/RelatedPredictionMarket";
import Description from "./components/Rules";
import { SelectChoice } from "./components/SelectChoice";
import { YesNoTabs } from "./components/YesNoTab";

export const PredictionMarketDetails = () => {
  const [isViewAllRanks, setIsViewAllRanks] = useState(false);
  const [participantChoiceRanks, setParticipantChoiceRanks] = useState<
    MarketRankLive["ranks"] | MarketRankEnded["ranks"]
  >([]);
  const [selectedChoice, setSelectedChoice] = useState<PredictionMarketChoice>();
  const [isEntrySheetOpen, setIsEntrySheetOpen] = useState(false);
  const [marketType, setMarketType] = useState<PredictionMarketType>(PredictionMarketType.YESNO);
  const [latestSnapshot, setLatestSnapshot] = useState<PredictionMarketSnapshot | null>(null);

  const [sortedChoices, setSortedChoices] = useState<Choice[]>([]);
  const [selectedChoiceIds, setSelectedChoiceIds] = useState<string[]>([]);
  const [predictionMarketSnapshots, setPredictionMarketSnapshots] = useState<PredictionMarketSnapshot[]>([]);

  const [listChoices, setListChoices] = useState<PredictionMarketChoice[]>([]);

  const [showDescription, setShowDescription] = useState(true);

  const [isChartLineVisible, setIsChartLineVisible] = useState(false);

  const { predictionMarketId } = useParams<{ predictionMarketId: string }>();
  if (!predictionMarketId) {
    throw new Error("predictionMarketId is required");
  }

  const predictionMarketRankingHandleError = useHandleError("予測市場ランキングの詳細を取得できませんでした");
  const authState = useAuthStore((state) => state.authState);

  const { data: predictionMarketRankings, isLoading: isPredictionMarketRankingLoading } =
    authState === "authenticated"
      ? useMyGetPredictionMarketRanking({
          predictionMarketId,
          swrOptions: {
            onError: predictionMarketRankingHandleError,
          },
        })
      : useGetPredictionMarketRanking({
          predictionMarketId,
          swrOptions: {
            onError: predictionMarketRankingHandleError,
          },
        });
  const {
    data: predictionMarketHistory,
    error: errorPredictionMarketHistory,
    isLoading: isLoadingPredictionMarketHistory,
  } = useGetPredictionMarketHistory({
    predictionMarketId,
  });

  useEffect(() => {
    if (!predictionMarketRankings?.marketRanks) return;

    if (predictionMarketHistory) {
      const choices = predictionMarketHistory.predictionMarket.predictionMarketChoices.map((choice) => ({
        id: choice.id,
        name: choice.name,
      }));

      const sortedChoices = extractChoices(predictionMarketHistory.predictionMarketHistory).sortedChoices;
      setSortedChoices(sortedChoices);
      if (marketType === PredictionMarketType.YESNO) {
        setSelectedChoiceIds([sortedChoices[0].id]);
      } else {
        setSelectedChoiceIds(sortedChoices.map((choice) => choice.id).slice(0, 4));
      }

      setListChoices(choices);
      if (predictionMarketRankings.status === "ended") {
        const winningChoiceId = predictionMarketRankings.winningChoice?.id;
        const defaultChoice = choices.find((c) => c.id === winningChoiceId) || choices[0];
        setSelectedChoice(defaultChoice);
      } else {
        setSelectedChoice(choices[0]);
      }
      setMarketType(getPredictionMarketType(choices));
      setLatestSnapshot(
        predictionMarketHistory.predictionMarketHistory[predictionMarketHistory.predictionMarketHistory.length - 1],
      );

      setPredictionMarketSnapshots(predictionMarketHistory.predictionMarketHistory);
    }
  }, [predictionMarketRankings, predictionMarketHistory, marketType]);

  useEffect(() => {
    if (!predictionMarketRankings?.marketRanks) return;
    if (!selectedChoice) return;
    const marketRanks = predictionMarketRankings.marketRanks;

    const matched = (marketRanks as MarketRankLive[]).find(
      (item) => item.predictionMarketChoiceId === selectedChoice.id,
    );
    const ranks = matched ? [...matched.ranks].sort((a, b) => a.rank - b.rank) : [];

    setParticipantChoiceRanks(ranks);
  }, [predictionMarketRankings, selectedChoice]);

  // Execute showData when LoadingSkeleton disappears
  useEffect(() => {
    if (
      !isPredictionMarketRankingLoading &&
      !isLoadingPredictionMarketHistory &&
      listChoices.length > 0 &&
      authState !== "fetchingUser"
    ) {
      setTimeout(() => {
        setIsChartLineVisible(true);
      }, 500);
    }
  }, [isPredictionMarketRankingLoading, isLoadingPredictionMarketHistory, listChoices.length, authState]);

  if (
    isPredictionMarketRankingLoading ||
    isLoadingPredictionMarketHistory ||
    listChoices.length === 0 ||
    authState === "fetchingUser"
  ) {
    return <LoadingSkeleton />;
  }

  if (!predictionMarketRankings || !predictionMarketHistory) {
    if (errorPredictionMarketHistory) {
      return <div className="w-full h-full flex items-center justify-center">Error loading data</div>;
    }
    return <div className="w-full h-full flex items-center justify-center">No data available</div>;
  }

  const yesId =
    marketType === PredictionMarketType.YESNO && sortedChoices.length > 0
      ? sortedChoices.find((choice) => choice.name.toUpperCase() === getYesNoLabel(choice))?.id
      : undefined;

  const totalPoints =
    latestSnapshot?.predictionChoiceStates?.reduce((acc, choice) => acc + Number(choice.totalFreePoints), 0) ?? 0;

  const isLive = predictionMarketRankings.status === "live";
  const isPrizeDistributed = predictionMarketRankings.status === "ended";
  const marketStatus = predictionMarketHistory.predictionMarket.status;

  return (
    <div className="w-full h-full bg-white relative mb-12">
      <PredictionMarketOGP market={predictionMarketHistory.predictionMarket} />

      {predictionMarketHistory.predictionMarket.primaryImageURL && (
        <div className="w-full h-[120px]">
          <img
            src={predictionMarketHistory.predictionMarket.primaryImageURL}
            alt="round-image"
            className="h-full w-full object-cover"
          />
        </div>
      )}

      <div className="h-10 w-full flex justify-between items-center py-[9px] px-4">
        <div className="flex items-center">
          {predictionMarketHistory.predictionMarket.label && (
            <Tag text={predictionMarketHistory.predictionMarket.label} />
          )}
          <div className="flex items-center ml-2">
            <ClockIcon className="text-dark-gray" />
            <DeadlineTime
              entryCloseTime={new Date(predictionMarketHistory.predictionMarket.entryCloseAt).getTime()}
              className="text-dark-gray font-medium text-xs"
            />
            {isPrizeDistributed && <span className="ml-1 text-dark-gray font-medium text-xs">終了</span>}
          </div>
        </div>

        <div className="flex justify-between items-center" onClick={(e) => e.stopPropagation()}>
          <EntryShareButton market={predictionMarketHistory.predictionMarket} className="text-dark-gray w-5 h-5" />
        </div>
      </div>

      <div className="pt-[6px] px-5 gap-5 flex flex-col">
        <h1 className="font-extrabold text-lg text-black w-full line-clamp-2">
          {predictionMarketHistory.predictionMarket.title}
        </h1>

        <div className="w-full" key={`${predictionMarketHistory.predictionMarket.id}`}>
          {latestSnapshot && (
            <MultipleChoicesGraph
              selectedChoiceIds={selectedChoiceIds}
              choices={latestSnapshot.predictionChoiceStates
                .map((choice) => ({
                  id: choice.choiceId,
                  name: choice.choiceName,
                  probability: choice.probability,
                }))
                .sort((a, b) => b.probability - a.probability)}
              showAll
            />
          )}
          {isLive ? (
            <PredictionChart
              predictionMarketSnapshots={predictionMarketSnapshots}
              defaultShowChartsIds={yesId ? [yesId] : []}
              setChoiceIds={setSelectedChoiceIds}
              realEndTime={predictionMarketHistory.predictionMarket.realEndTime}
              isLineVisible={isChartLineVisible}
            />
          ) : (
            <>
              <div className="mb-7">
                <PredictionChart
                  predictionMarketSnapshots={predictionMarketSnapshots}
                  defaultShowChartsIds={yesId ? [yesId] : []}
                  setChoiceIds={setSelectedChoiceIds}
                  realEndTime={predictionMarketHistory.predictionMarket.realEndTime}
                  isLineVisible={isChartLineVisible}
                />
              </div>
              <ChartList
                choices={sortedChoices}
                predictionMarketSnapshots={predictionMarketSnapshots}
                winningChoiceId={predictionMarketRankings.winningChoice.id}
                realEndTime={predictionMarketHistory.predictionMarket.realEndTime}
              />
            </>
          )}
        </div>

        {/* CHOICES TO ENTER WITH  */}
        {latestSnapshot && isLive && (
          <>
            <MultipleChoices
              isShowLabel={true}
              isFixedYesNo={marketType === PredictionMarketType.YESNO}
              choices={latestSnapshot.predictionChoiceStates
                .map((choice) => ({
                  id: choice.choiceId,
                  name: choice.choiceName,
                  probability: choice.probability,
                }))
                .sort((a, b) => b.probability - a.probability)}
              onButtonClick={(choiceId: string) => {
                if (marketStatus === "Closed") return;
                const choice = listChoices.find((c) => c.id === choiceId);
                if (!choice) {
                  throw new Error("Choice not found");
                }
                setSelectedChoice(choice);
                setIsEntrySheetOpen(true);
              }}
              showAll
            />
            <Drawer open={isEntrySheetOpen} onOpenChange={setIsEntrySheetOpen} repositionInputs={false}>
              <DrawerContent
                className="mx-auto flex flex-col justify-center md:w-2/3 lg:w-1/2 border-none"
                dragClassName="h-1 mt-2 mb-4 bg-disabled/70"
              >
                <div className="flex flex-col justify-center w-full border-none">
                  <EntryPopup
                    predictionMarket={predictionMarketHistory.predictionMarket}
                    selectedChoice={
                      selectedChoice ?? {
                        id: latestSnapshot.predictionChoiceStates[0].choiceId,
                        name: latestSnapshot.predictionChoiceStates[0].choiceName,
                      }
                    }
                  />
                </div>
              </DrawerContent>
            </Drawer>
          </>
        )}

        {/* MONEY, NUM ENTRIES */}
        <div className="h-9 flex justify-between items-center">
          <div className="flex items-center gap-5 h-[20.9px]">
            <div className="flex items-center gap-[2.57px]">
              <ThumbaCoin className="min-w-6 min-h-6" />
              <span className="text-black font-bold text-lg">{formatNumberWithCommas(totalPoints)} pt</span>
            </div>

            <div className="flex items-center gap-[7px]">
              <div className="flex items-center gap-[2.82px]">
                <GroupUserIcon />
                <span className="text-dark-gray text-xs">参加数</span>
              </div>
              <span className="text-dark-gray text-xs font-bold">
                {formatNumberWithCommas(predictionMarketHistory.numberOfParticipants)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-5">
          <Description
            showDescription={showDescription}
            setShowDescription={setShowDescription}
            description={predictionMarketHistory.predictionMarket.description}
          />
        </div>

        {
          // isLive &&
          marketType === PredictionMarketType.YESNO ? (
            <YesNoTabs choices={listChoices} selectedChoice={selectedChoice} setSelectedChoice={setSelectedChoice} />
          ) : (
            <SelectChoice
              selectedChoice={selectedChoice}
              listChoices={listChoices}
              setSelectedChoice={setSelectedChoice}
            />
          )
        }

        <ChoiceRanking isEnd={isPrizeDistributed} participantChoiceRanks={participantChoiceRanks} />

        <div className="flex justify-end items-center" onClick={() => setIsViewAllRanks((prev) => !prev)}>
          <div className="flex gap-[7px] items-center justify-center cursor-pointer">
            <div className="text-right font-medium text-[12px] leading-[17.88px] tracking-tighter text-secondary">
              全ランキングを見る
            </div>
            <ChevronUp className="rotate-90 text-secondary" />
          </div>
        </div>

        <div className="h-[1px] bg-light-gray w-full" />
      </div>

      {predictionMarketHistory.predictionMarket.label && (
        <div className="mt-[30px] px-5 pt-[6px] h-[337px] w-full bg-gray">
          <RelatedPredictionMarket label={predictionMarketHistory.predictionMarket.label} />
        </div>
      )}

      <AllRankingsModal
        predictionMarket={predictionMarketHistory.predictionMarket}
        isViewAllRankings={isViewAllRanks}
        setIsViewAllRankings={setIsViewAllRanks}
        selectedChoice={selectedChoice}
        listChoices={listChoices}
        setSelectedChoice={setSelectedChoice}
        participantChoiceRanks={participantChoiceRanks}
      />
    </div>
  );
};

const LoadingSkeleton = () => {
  return (
    <div className="bg-white">
      <Skeleton className="w-full h-[120px]" />

      <div className="px-4">
        <div className="flex justify-between py-2">
          <div className="flex gap-2">
            <Skeleton className="w-20 h-6" />
            <Skeleton className="w-10 h-6" />
          </div>
          <Skeleton className="h-6 w-6 " />
        </div>

        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-[300px] w-full mt-3" />
        <Skeleton className="h-12 w-full mt-2" />
        <Skeleton className="h-10 w-1/3 mt-2" />

        <Skeleton className="h-10 w-full mt-4 hidden md:block" />
        <Skeleton className="h-10 w-full mt-4 hidden md:block" />

        <div className="flex gap-2 mt-3 p-5">
          <Skeleton className="h-14 w-1/2" />
          <Skeleton className="h-14 w-1/2" />
        </div>
      </div>
    </div>
  );
};
